import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ErrorBoundary } from './ErrorBoundary';
import { useQueryClient } from '@tanstack/react-query';
import {
  BarChart3, Bolt, Gavel, BookOpen, CalendarDays, ClipboardCheck, ClipboardList, FileSignature, Home, LogOut, Menu, MessagesSquare, Package, Plus, Receipt, Search, Settings, ShieldCheck, Users, Wrench, X,
} from 'lucide-react';
import { api } from '../lib/api';
import { can, useMeta } from '../lib/hooks';
import { cx } from './ui';
import { MainWidthContext } from './mainWidth';
import { ROLE_INFO, type Role } from '@setcom/core';
import type { Meta } from '../lib/types';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  roles?: string[];
  mobile?: boolean;
  /** แสดงเมื่อ server บอกว่าใช่ (ไม่ใช้ roles — เช่น เจ้าของเท่านั้น admin ก็ไม่เห็น) */
  only?: (meta: Meta) => boolean;
  /** แสดงเมื่อเปิดฟีเจอร์ใน meta เท่านั้น */
  feature?: 'techGroupUpdates';
}

const NAV: NavItem[] = [
  { to: '/', label: 'หน้าแรก', icon: <Home />, mobile: true },
  { to: '/decisions', label: 'รอคุณฟลุ๊คตัดสินใจ', icon: <Gavel />, only: (m) => m.decisionsOwner === true },
  { to: '/jobs', label: 'งานทั้งหมด', icon: <ClipboardList />, mobile: true },
  { to: '/schedule', label: 'ตารางนัด', icon: <CalendarDays />, mobile: true },
  { to: '/customers', label: 'ลูกค้า', icon: <Users />, mobile: true },
  { to: '/admin-queue', label: 'รอแอดมิน', icon: <ClipboardCheck />, roles: ['office', 'accounting'] },
  { to: '/tech-group', label: 'รอแอดมินจับคู่', icon: <MessagesSquare />, roles: ['office'], feature: 'techGroupUpdates' },
  { to: '/billing', label: 'รอวางบิล', icon: <Receipt />, roles: ['office', 'accounting'] },
  { to: '/assets', label: 'อุปกรณ์ลูกค้า', icon: <Package /> },
  { to: '/contracts', label: 'สัญญา MA', icon: <FileSignature />, roles: ['office', 'accounting', 'viewer', 'technician'] },
  { to: '/ev-reports', label: 'รายงาน EV', icon: <Bolt />, roles: ['office', 'technician'] },
  { to: '/rma', label: 'ส่งเคลม', icon: <ShieldCheck /> },
  { to: '/ledger', label: 'เงินเข้า/ออก', icon: <BookOpen />, roles: ['office', 'accounting'] },
  { to: '/reports', label: 'รายงาน', icon: <BarChart3 />, roles: ['office', 'accounting', 'viewer'] },
  { to: '/settings', label: 'ตั้งค่า', icon: <Settings />, roles: [] },
];

export function Layout() {
  const { data: meta } = useMeta();
  const role = meta?.me.role;
  const nav = NAV.filter((n) => (n.only ? !!meta && n.only(meta) : (!n.roles || can(role, ...n.roles)) && (!n.feature || !!meta?.[n.feature])));
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onNewJob = location.pathname === '/jobs/new';
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [fullWidth, setFullWidth] = useState(false);

  const logout = async () => {
    await api.post('/auth/logout').catch(() => undefined);
    qc.clear();
    navigate('/login');
  };

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    cx('flex min-h-13 items-center gap-3 rounded-2xl px-4 py-3 font-display text-lg font-semibold transition [&>svg]:size-6', isActive ? 'bg-brand text-white shadow-sm' : 'text-ink hover:bg-brand-soft');

  return (
    <div className="flex min-h-dvh">
      {/* แถบข้างสำหรับจอใหญ่ — เป็นคอลัมน์สูงเท่าหน้า (พื้นขาวยาวถึงล่างสุด) เนื้อในติดอยู่กับที่ */}
      <aside className="no-print hidden w-72 shrink-0 border-r border-line bg-white md:block">
        <div className="sticky top-0 flex h-dvh flex-col gap-1 p-4">
        <div className="mb-4 flex items-center gap-3 px-2 pt-1">
          <div className="grid size-12 place-items-center rounded-2xl bg-brand text-white">
            <Wrench className="size-7" />
          </div>
          <div>
            <div className="font-display text-2xl font-bold leading-tight">SETCOM</div>
            <div className="text-sm text-muted">ระบบงานซ่อม & บริการ</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/jobs/new')}
          className={cx('mb-3 flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-st-green font-display text-xl font-bold text-white shadow-md transition hover:brightness-95 active:scale-[0.98]', onNewJob && 'ring-4 ring-st-green/30 ring-offset-2')}
        >
          <Plus className="size-7" /> เปิดงานใหม่
        </button>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => linkCls({ isActive: isActive && !(n.to === '/jobs' && onNewJob) })}>
              {n.icon}
              {n.label}
            </NavLink>
          ))}
        </nav>
        {meta && (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-2xl bg-paper px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold">{meta.me.name}</div>
              <div className="text-sm text-muted">{ROLE_INFO[meta.me.role as Role]?.label}</div>
            </div>
            <button type="button" onClick={logout} className="grid size-11 place-items-center rounded-xl text-muted hover:bg-white hover:text-st-rose" aria-label="ออกจากระบบ" title="ออกจากระบบ">
              <LogOut className="size-6" />
            </button>
          </div>
        )}
        </div>
      </aside>
      <div className="min-w-0 flex-1">

      {/* แถบบน: ค้นหา */}
      <header className="no-print sticky top-0 z-20 border-b border-line bg-paper/90 backdrop-blur">
        <form
          className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
          }}
        >
          <div className="grid size-11 place-items-center rounded-xl bg-brand text-white md:hidden">
            <Wrench className="size-6" />
          </div>
          <label className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-6 -translate-y-1/2 text-muted" />
            <input
              id="global-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา เลขงาน ชื่อลูกค้า เบอร์โทร Serial…"
              className="w-full rounded-2xl border-2 border-line bg-white py-3 pl-13 pr-4 text-lg focus:border-brand focus:outline-none"
            />
          </label>
        </form>
      </header>

      <main className={cx('mx-auto px-4 pb-32 pt-5 md:pb-12', fullWidth ? 'max-w-none lg:px-6' : 'max-w-6xl')}>
        <MainWidthContext value={setFullWidth}>
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </MainWidthContext>
      </main>
      </div>

      {/* ปุ่มเปิดงานลอย (มือถือ) */}
      <button
        type="button"
        onClick={() => navigate('/jobs/new')}
        className="no-print fixed bottom-24 right-4 z-30 grid size-16 place-items-center rounded-full bg-st-green text-white shadow-xl active:scale-95 md:hidden"
        aria-label="เปิดงานใหม่"
      >
        <Plus className="size-9" />
      </button>

      {/* แถบล่างมือถือ */}
      <nav className="no-print fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        {nav
          .filter((n) => n.mobile)
          .map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => cx('flex min-h-16 flex-col items-center justify-center gap-0.5 text-sm font-semibold [&>svg]:size-7', isActive && !(n.to === '/jobs' && onNewJob) ? 'text-brand' : 'text-muted')}
            >
              {n.icon}
              {n.label.replace('งานทั้งหมด', 'งาน').replace('ตารางนัด', 'นัด')}
            </NavLink>
          ))}
        <button type="button" onClick={() => setMoreOpen(true)} className="flex min-h-16 flex-col items-center justify-center gap-0.5 text-sm font-semibold text-muted [&>svg]:size-7">
          <Menu />
          เพิ่มเติม
        </button>
      </nav>

      {moreOpen && (
        <div className="no-print fixed inset-0 z-40 bg-ink/40 md:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <span className="font-display text-xl font-bold">{meta?.me.name}</span>
              <button type="button" onClick={() => setMoreOpen(false)} className="grid size-11 place-items-center rounded-full hover:bg-st-slate-soft" aria-label="ปิด">
                <X className="size-6" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {nav
                .filter((n) => !n.mobile)
                .map((n) => (
                  <NavLink key={n.to} to={n.to} onClick={() => setMoreOpen(false)} className={linkCls}>
                    {n.icon}
                    {n.label}
                  </NavLink>
                ))}
              <button type="button" onClick={logout} className="flex min-h-13 items-center gap-3 rounded-2xl px-4 py-3 font-display text-lg font-semibold text-st-rose hover:bg-st-rose-soft [&>svg]:size-6">
                <LogOut /> ออกจากระบบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
